import { RoutingItem } from "../../_UILibrary/Navigation"
import Routes from '@utils/routes'
import { PermissionsContextType } from "./PermissionsContext"

type Truthy<T> = T extends false | '' | 0 | null | undefined ? never : T; // from lodash

function truthy<T>(value: T): value is Truthy<T> {
  return !!value;
}

let ID_TRACKER = Math.floor(Math.random() * 1000)

function cleanItems(items: (RouteWithSub | false)[]): RouteWithSub[] {
  return items.filter(truthy).map((item) => {
    item.id = ++ID_TRACKER;
    return item;
  });
}

function requiredRoutePermissions(required: Partial<PermissionsContextType>, permissions: PermissionsContextType) {
  return Object.keys(required).every(key => permissions[key])
}

type RouteWithSub = RoutingItem & { id?: number, title?: string, breadcrumb?: string, href?: string, backHref?: string, backLabel?: string, subNav?: RouteWithSub[], matchPaths?: string[] }

type GetNavItems = (props: NavRouteProps) => RouteWithSub[]

type NavRouteProps = {
  permissions: PermissionsContextType
  tenantSlug: string
  currentUserId?: string,
  userType?: "Client" | "WealthManager" | "Paraplanner" | "UserProfile",
  wealthManagerId?: string
  clientId?: string
  clientName?: string,
  isProspect?: boolean,
  jointScenarioId?: string,
  isNewJointScenario?: boolean,
}

const getHomeNavItems: GetNavItems = ({
  permissions,
  tenantSlug,
  userType,
  currentUserId,
  jointScenarioId
}) => {
  const requiredPermissions = []

  switch (userType) {
    case "Client":
      // Individuals have no "Home" wrapper: the section's items are promoted to
      // top-level nav items. Home/root is the dashboard (Current Plan).
      return cleanItems([
        {
          label: "Current Plan",
          icon: "home",
          path: Routes.clientDashboardPath({ tenantSlug, clientId: currentUserId }),
        },
        !!jointScenarioId && {
          label: "Joint Plan",
          icon: "two-people",
          path: Routes.jointScenarioPath({ tenantSlug, clientId: currentUserId, jointScenarioId })
        },
        {
          label: "Profile",
          icon: "user-circle",
          path: Routes.clientProfilePath({ tenantSlug, clientId: currentUserId }),
        },
        {
          label: "Scenarios",
          icon: "layout",
          path: Routes.clientScenariosPath({ tenantSlug, clientId: currentUserId })
        }
      ])
    case "UserProfile":
      // Home links straight to the dashboard; no redundant "Dashboard" sub-item.
      return cleanItems([
        {
          label: 'Home',
          icon: "home",
          path: Routes.userProfileDashboardPath({ tenantSlug }),
        }
      ])
    default:
      // Home links straight to the dashboard (no "Dashboard" sub-item). Reminders
      // is promoted to its own top-level item (see getRemindersNavItems).
      return cleanItems([
        {
          label: 'Home',
          icon: "home",
          path: Routes.wealthManagerDashboardPath({ tenantSlug, wealthManagerId: currentUserId }),
          subNav: cleanItems([
            permissions["task:index"] && {
              label: "Tasks",
              path: Routes.tasksPath({ tenantSlug, wealthManagerId: currentUserId }),
              icon: "check-mark-in-square",
            }
          ])
        }
      ])
  }
}

const getRemindersNavItems: GetNavItems = ({
  permissions,
  tenantSlug,
  userType,
  currentUserId
}) => {
  if (userType === "Client" || userType === "UserProfile") return []
  if (!permissions["reminder:index"]) return []

  return cleanItems([
    {
      label: "Reminders",
      icon: "alarm-clock",
      path: Routes.remindersPath({ tenantSlug, wealthManagerId: currentUserId }),
    }
  ])
}

const getClientsNavItems: GetNavItems = ({
  permissions,
  tenantSlug,
  clientId,
  clientName,
  isProspect = false,
  jointScenarioId,
  isNewJointScenario = false
}) => {
  const requiredPermissions: Partial<PermissionsContextType> = {
    "client:index": true,
  }

  if (!requiredRoutePermissions(requiredPermissions, permissions)) {
    return []
  }

  // Prospects reuse the /clients/:id/* detail routes, so only open the client
  // detail sub-context for a real client — otherwise the Clients section would
  // hijack a prospect (which the Prospects section handles instead).
  const showClientContext = Boolean(clientId) && !isProspect

  return cleanItems([
    {
      label: 'Clients',
      icon: "two-people",
      subNav: cleanItems([
        {
          label: "Clients",
          icon: "two-people",
          path: Routes.clientsPath({ tenantSlug }),
          title: showClientContext ? clientName : undefined,
          breadcrumb: showClientContext && clientName ? `${clientName.slice(0, 5)}...` : undefined,
          backHref: Routes.clientsPath({ tenantSlug }),
          backLabel: "All clients",
          subNav: !showClientContext ? undefined : cleanItems([
            {
              label: "Current Plan",
              path: Routes.clientDashboardPath({ tenantSlug, clientId }),
            },
            (permissions["joint-plan:view"] && !!jointScenarioId) && {
              label: "Joint Plan",
              path: Routes.jointScenarioPath({ tenantSlug, clientId, jointScenarioId })
            },
            (permissions["joint-plan:create"] && isNewJointScenario) && {
              label: "New Joint Scenario",
              path: Routes.newJointScenarioPath({ tenantSlug, clientId })
            },
            permissions["client:profile"] && {
              label: "Profile",
              path: Routes.clientProfilePath({ tenantSlug, clientId }),
              // The edit form lives at a different URL but is part of the same
              // profile context — match it here so /clients/:id/edit keeps the
              // "Clients > name > Profile" context (and highlights Profile)
              // instead of losing the sub-nav entirely.
              matchPaths: [Routes.editClientPath({ clientId })],
            },
            {
              label: "Scenarios",
              path: Routes.clientScenariosPath({ tenantSlug, clientId })
            }
          ])
        },
        showClientContext && {
          label: "Referrals",
          path: Routes.referralsPath({ tenantSlug, clientId }),
          icon: "two-people",
        }
      ])
    }
  ])
}

const getProspectsNavItems: GetNavItems = ({
  permissions,
  tenantSlug,
  clientId,
  clientName,
  isProspect = false
}) => {
  const requiredPermissions: Partial<PermissionsContextType> = {
    "prospect:index": true,
  }

  if (!requiredRoutePermissions(requiredPermissions, permissions)) {
    return []
  }

  // A prospect is a Client (prospect: true) whose detail pages live under the
  // /clients/:id/* routes. When one is open, mirror the client detail sub-nav so
  // there is a visible context (name + back-link + section links), but rooted in
  // the Prospects section with an "All prospects" back-link.
  const showProspectContext = Boolean(clientId) && isProspect

  return cleanItems([
    {
      label: 'Prospects',
      icon: "person-plus",
      subNav: cleanItems([
        {
          label: "Prospects",
          icon: "person-plus",
          path: Routes.prospectsPath({ tenantSlug }),
          title: showProspectContext ? clientName : undefined,
          breadcrumb: showProspectContext && clientName ? `${clientName.slice(0, 5)}...` : undefined,
          backHref: Routes.prospectsPath({ tenantSlug }),
          backLabel: "All prospects",
          // A prospect (unlike a client) has only one viable detail route: their
          // profile/details page — which is the edit form they land on. Point the
          // single "Profile" item at editClientPath so the context also activates
          // on that landing page. No Current Plan / Scenarios: not viable yet.
          subNav: !showProspectContext ? undefined : cleanItems([
            {
              label: "Profile",
              path: Routes.editClientPath({ clientId }),
            }
          ])
        }
      ])
    }
  ])
}

const getOrganisationNavItems: GetNavItems = ({
  permissions,
}) => {
  const requiredPermissions: Partial<PermissionsContextType> = {
    "organization:show": true,
  }

  if (!requiredRoutePermissions(requiredPermissions, permissions)) {
    return []
  }

  return cleanItems([
    {
      label: 'Organisation',
      icon: "organisation",
      subNav: cleanItems([
        permissions["organization-dashboard:show"] && {
          label: "Dashboard",
          path: Routes.organizationDashboardPath(),
          icon: "hierarchy",
        },
        permissions["user-profile:index"] && {
          label: "User Roles",
          path: "#",
          icon: "tophat",
        },
      ])
    }
  ])
}

const getTeamNavItems: GetNavItems = ({
  permissions,
  tenantSlug
}) => {
  const requiredPermissions: Partial<PermissionsContextType> = {
    "user-profile:index": true,
    "paraplanner:index": true,
  }

  if (!Object.keys(requiredPermissions).some(key => permissions[key])) {
    return []
  }

  return cleanItems([
    {
      label: 'Team',
      icon: "two-people-circle",
      subNav: cleanItems([
        permissions["user-profile:index"] && {
          label: "Advisors",
          path: "#",
          icon: "two-people",
        },
        permissions["paraplanner:index"] && {
          label: "Paraplanners",
          path: Routes.paraplannersPath({ tenantSlug }),
          icon: "two-people",
        },
      ])
    }
  ])
}

const getFspAdminNavItems: GetNavItems = ({
  permissions,
}) => {
  if (!permissions["fsp-admin-dashboard:show"]) {
    return []
  }

  return cleanItems([
    {
      label: 'FSP Admin',
      icon: "organisation",
      subNav: cleanItems([
        {
          label: "Dashboard",
          path: Routes.fspAdminDashboardPath(),
          icon: "hierarchy",
        },
      ])
    }
  ])
}

const getNavigationRoutes = (props: NavRouteProps): RouteWithSub[] => {
  return [
    ...getHomeNavItems(props),
    ...getRemindersNavItems(props),
    ...getClientsNavItems(props),
    ...getProspectsNavItems(props),
    ...getOrganisationNavItems(props),
    ...getTeamNavItems(props),
    ...getFspAdminNavItems(props)
  ]
}

export type { RouteWithSub, NavRouteProps }
export { getNavigationRoutes, cleanItems as cleanNavItems }
